/**
 * View component for /sites
 *
 * Generic site list view. Defaults to 'all' with:
 * this.props.dispatch(siteActions.fetchListIfNeeded());
 *
 * NOTE: See /product/views/ProductList.js.jsx for more examples
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// import actions
import * as siteActions from '../siteActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';

// import global components
import { SearchInput } from '../../../global/components/forms';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";

// import utilities
import filterUtils from '../../../global/utils/filterUtils';

// import site components
import SiteListItem from '../components/SiteListItem.js.jsx';

// import site css modules
import siteStyles from '../siteModuleStyles.css';

class SiteList extends Base {
  constructor(props) {
    super(props);
    this.state = {
      perPage: 50
      , queryText: this.props.query || ''
    };
    this._bind(
      '_handleQuery'
      , '_handleSetPagination'
      , '_handleSort'
      , '_resetPagination'
      , '_setPerPage'
    );
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    // fetch a list of your choice
    dispatch(siteActions.fetchListIfNeeded('all'))
    dispatch(sponsorActions.fetchListIfNeeded('all'))
    dispatch(studyActions.fetchListIfNeeded('all'));
    dispatch(siteActions.setFilter({query: '', sortBy: 'name'}));
    dispatch(siteActions.setPagination({page: 1, per: 50}));
  }

  _handleSort(sortBy) {
    const { dispatch, match, siteList } = this.props;
    let newFilter = siteList.filter;
    if(siteList.filter.sortBy && siteList.filter.sortBy.indexOf("-") < 0) {
      sortBy = "-" + sortBy;
    } else {
      sortBy = sortBy.substring(0)
    }
    newFilter.sortBy = sortBy;
    dispatch(siteActions.setFilter(newFilter));
  }

  _resetPagination() {
    const { dispatch, match } = this.props;
    dispatch(siteActions.setPagination({
      page: 1,
      per: parseInt(this.state.perPage)
    }));
  }

  _setPerPage(per) {
    const { dispatch, match, siteList } = this.props;
    var newPagination = siteList.pagination;
    newPagination.per = parseInt(per);
    newPagination.page = 1;
    dispatch(siteActions.setPagination(newPagination));
    this.setState({perPage: per});
  }

  _handleSetPagination(newPagination) {
    const { dispatch, match } = this.props;
    dispatch(siteActions.setPagination(newPagination));
  }

  _handleQuery(e) {
    const { dispatch, match, siteList } = this.props;
    // always defaulting the page to page 1 so we can see our results
    var pagination = {};
    pagination.page = 1;
    pagination.per = siteList.pagination.per;
    this._handleSetPagination(pagination);
    // continue query logic
    dispatch(siteActions.setFilter({
      query: e.target.value.toLowerCase()
      , sortBy: siteList.filter.sortBy
    }));
  }

  render() {
    const {
      paginatedList
      , siteList
      , siteListItems
      , siteStore
      , sortedList
      , sponsorStore
      , studyStore
    } = this.props;
    const { queryText } = this.state;

    /**
     * Retrieve the list information and the list items for the component here.
     *
     * NOTE: if the list is deeply nested and/or filtered, you'll want to handle
     * these steps within the mapStoreToProps method prior to delivering the
     * props to the component.  Othwerwise, the render() action gets convoluted
     * and potentially severely bogged down.
     */

    /**
     * NOTE: isEmpty is is usefull when the component references more than one
     * resource list.
     */
    const isEmpty = !siteListItems || !siteList || siteListItems.length === 0;
    const sortedEmpty = sortedList.length === 0;

    return (
      <div className="flex">
        <Helmet
          title={"Site List | Pro-ficiency"}
        />
        <section className="section">
          <div className="yt-container">
            <h1> Sites
              <Link className="yt-btn small u-pullRight" to={'/admin/sites/new'}>NEW Site/Team</Link>
            </h1>
            <hr/>
            { isEmpty ?
                (
                  siteListItems && siteList.isFetching ?
                    <div className="u-centerText">
                      <div className="loading"></div>
                    </div>
                  :
                    <h2>Empty.</h2>
                )
              :
                <div style={{ opacity: siteList.isFetching ? 0.5 : 1 }}>
                  <div className="yt-toolbar">
                    <div className="yt-tools right">
                      <div className="search">
                        <SearchInput
                          name="query"
                          value={siteList.filter.query}
                          change={this._handleQuery}
                          placeholder="Search..."
                          required={false}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="table-wrapper">
                    <table className="yt-table striped">
                      <caption>
                        { siteList.filter.query.length > 0 ?
                          <span>Filtered Sites &mdash; {sortedList.length} of {Object.keys(siteList.items).length}</span>
                          :
                            <span>All Sites &mdash; {Object.keys(siteList.items).length}</span>
                        }
                        <div className="per-page-select u-pullRight">
                          <label>Show per page: </label>
                          <select
                            name="numPerPage"
                            onChange={(e) => this._setPerPage(e.target.value)}
                            value={this.state.perPage}
                          >
                            <option value={25}> 25 </option>
                            <option value={50}> 50 </option>
                            <option value={100}> 100 </option>
                          </select>
                        </div>
                      </caption>
                      <thead>
                        <tr>
                          <th className="_25 sortable" onClick={()=> this._handleSort('name')}>
                            Name
                            {siteList.filter.sortBy === 'name' ?
                              <i className="ion ion-ios-arrow-down" />
                              : siteList.filter.sortBy === '-name' ?
                                <i className="ion ion-ios-arrow-up" />
                              : null
                            }
                          </th>
                          <th className="_25 sortable" onClick={()=> this._handleSort('number')}>
                            Number
                            {siteList.filter.sortBy === 'number' ?
                              <i className="ion ion-ios-arrow-down" />
                              : siteList.filter.sortBy === '-number' ?
                                <i className="ion ion-ios-arrow-up" />
                              : null
                            }
                          </th>
                          <th className="_25">Sponsor</th>
                          <th className="_25">Study</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          paginatedList.map((site, i) =>
                            <SiteListItem
                              key={site._id + i}
                              site={site}
                              sponsor={sponsorStore.byId[site._organization]}
                              study={studyStore.byId[site._group]}
                            />
                          )
                        }
                      </tbody>
                    </table>
                    { sortedEmpty ?
                        null
                      :
                        <Pagination
                          totalPages={ Math.floor(sortedList.length / siteList.pagination.per) + 1}
                          pagination={siteList.pagination}
                          setPagination={this._handleSetPagination}
                        />
                    }
                  </div>
                </div>
            }
          </div>
        </section>
      </div>
    )
  }
}

SiteList.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  const siteStore = store.site;
  const siteList = store.site.lists.all;
  const siteListItems = siteStore.util.getList("all");

  const sponsors = store.sponsor.util.getList('all');
  const studies = store.study.util.getList('all');

  let sortedList = [];
  let paginatedList = [];
  let filteredByQuery = [];
  if (siteList) {

    const pagination = siteList.pagination;
    const filter = siteList.filter;
    const query = filter ? filter.query : '';
    const sortBy = filter ? filter.sortBy : 'name';

    // FILTER BY QUERY
    var queryTestString = ("" + query).toLowerCase().trim();
    // queryTestString = queryTestString.replace(/[^a-zA-Z0-9]/g,''); // replace all non-characters and numbers
    filteredByQuery = siteList.items.filter((siteId) => {
      return filterUtils.filterSite(queryTestString, siteStore.byId[siteId], sponsors, studies);
    });

    // SORT THE LIST
    sortedList = filteredByQuery.map((item) => {
      var newItem = siteStore.byId[item];
      return newItem;
    });
    switch(sortBy) {
      case 'name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['asc']);
        break;
      case '-name':
        sortedList = _.orderBy(sortedList, [item => item.name.toLowerCase()], ['desc']);
        break;
      case 'number':
        sortedList = _.orderBy(sortedList, [item => item.number.toLowerCase()], ['asc']);
        break;
      case '-number':
        sortedList = _.orderBy(sortedList, [item => item.number.toLowerCase()], ['desc']);
        break;
      default:
        sortedList = _.orderBy(sortedList, [item => item.email.toLowerCase()], ['asc']);
    }

    // apply pagination
    var start = (pagination.page - 1) * pagination.per;
    var end = start + pagination.per;
    paginatedList = _.slice(sortedList, start, end);

  }
  return {
    paginatedList
    , siteList
    , siteListItems
    , siteStore
    , sortedList
    , sponsorStore: store.sponsor
    , studyStore: store.study
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(SiteList)
);



// WEBPACK FOOTER //
// modules/site/views/AdminSiteList.js.jsx